/**
 * Admin panel path parsing and building.
 * Single source of truth for /admin/* URL shape and derived state.
 */

const ADMIN_BASE = '/admin';
const RESERVED_APPROVAL = new Set(['new', 'pending']);
const SECTIONS_WITH_SUBROUTES = new Set([
  'object-manager',
  'approval-processes',
  'process-builder',
  'flow-automation',
  'scheduled-jobs'
]);

export interface ParsedAdminPath {
  section: string;
  objectId?: string;
  approvalView?: 'list' | 'new' | 'pending' | 'detail';
  approvalId?: string;
  processView?: 'list' | 'create' | 'detail';
  processId?: string;
  flowView?: 'list' | 'create' | 'detail';
  flowId?: string;
  jobView?: 'list' | 'create' | 'detail';
  jobId?: string;
}

export function parseAdminPath(pathname: string): ParsedAdminPath {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const segments = normalized.split('/').filter(Boolean);
  const withoutAdmin = segments[0] === 'admin' ? segments.slice(1) : segments;

  const section = withoutAdmin[0] || 'setup-home';
  const result: ParsedAdminPath = { section };

  if (section === 'object-manager' && withoutAdmin[1]) {
    result.objectId = withoutAdmin[1];
  }

  if (section === 'approval-processes') {
    const second = withoutAdmin[1];
    if (second === 'new') {
      result.approvalView = 'new';
    } else if (second === 'pending') {
      result.approvalView = 'pending';
    } else if (second) {
      result.approvalView = 'detail';
      result.approvalId = second;
    } else {
      result.approvalView = 'list';
    }
  }

  if (section === 'process-builder') {
    const second = withoutAdmin[1];
    if (second === 'new') {
      result.processView = 'create';
    } else if (second) {
      result.processView = 'detail';
      result.processId = second;
    } else {
      result.processView = 'list';
    }
  }

  if (section === 'flow-automation') {
    const second = withoutAdmin[1];
    if (second === 'new') {
      result.flowView = 'create';
    } else if (second) {
      result.flowView = 'detail';
      result.flowId = second;
    } else {
      result.flowView = 'list';
    }
  }

  if (section === 'scheduled-jobs') {
    const second = withoutAdmin[1];
    if (second === 'new') {
      result.jobView = 'create';
    } else if (second) {
      result.jobView = 'detail';
      result.jobId = second;
    } else {
      result.jobView = 'list';
    }
  }

  return result;
}

export function buildAdminPath(
  section: string,
  options?: {
    objectId?: string;
    approvalView?: 'new' | 'pending' | 'detail';
    approvalId?: string;
    processId?: string;
    flowId?: string;
    jobId?: string;
  }
): string {
  if (!section || section === 'setup-home') {
    return ADMIN_BASE;
  }
  const base = `${ADMIN_BASE}/${section}`;
  if (!options) return base;

  if (section === 'object-manager' && options.objectId) {
    return `${base}/${encodeURIComponent(options.objectId)}`;
  }
  if (section === 'approval-processes') {
    if (options.approvalView === 'new') return `${base}/new`;
    if (options.approvalView === 'pending') return `${base}/pending`;
    if (options.approvalView === 'detail' && options.approvalId) {
      return `${base}/${encodeURIComponent(options.approvalId)}`;
    }
    return base;
  }
  if (section === 'process-builder') {
    if (options.processId === 'new') return `${base}/new`;
    if (options.processId) return `${base}/${encodeURIComponent(options.processId)}`;
    return base;
  }
  if (section === 'flow-automation') {
    if (options.flowId === 'new') return `${base}/new`;
    if (options.flowId) return `${base}/${encodeURIComponent(options.flowId)}`;
    return base;
  }
  if (section === 'scheduled-jobs') {
    if (options.jobId === 'new') return `${base}/new`;
    if (options.jobId) return `${base}/${encodeURIComponent(options.jobId)}`;
    return base;
  }
  return base;
}

export function getActiveSectionFromPath(pathname: string): string {
  return parseAdminPath(pathname).section;
}
